// @ts-strict-ignore
import type { FeedItemType } from '@/types';

export const formatResult = (
  result: FeedItemType,
  category: string,
  type = 'search'
): FeedItemType => {
  switch (category) {
    case 'bse_conf_calls':
      return {
        company_name: result.company_name || '',
        object_location: Array.isArray(result.link)
          ? result.link[0]
          : result.link,
        unix: result.unix,
        sentiment_link: result.sentiment_link,
        security_code: result.security_code,
        security_id: result.security_id,
        cin: result.cin,
        isin: result.isin,
        doc_id: result.doc_id,
        deeplink: result.deeplink,
        access_key: result.access_key,
        board_id: result.board_id,
        channel_id: result.channel_id,
        mime_type: result.mime_type,
        category: 'bse_conf_calls',
        category_tab: result.category_tab,
        selected: result.selected,
        source: 'bse_conf_calls',
        image_location: result.image_location,
        report_year: result.report_year,
        quarter: result.quarter,
        attachmentname: getAttachmentName(result), // attachment always exist
        indices: result.indices,
        secondary_category: result.secondary_category_ua,
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
        highlights: result.highlights,
        link_to_pdf: result.link_to_pdf,
        external_access_link: result.external_access_link,
        comment_id: result?.comment_id,
        num_pages: result?.num_pages,
      };
    case 'earnings_conf_calls':
      return {
        ...result,
        attachmentname: result.doc_id, // attachment always exist
        object_location: Array.isArray(result.link)
          ? result.link[0]
          : result.link,
        mime_type_label: result?.mime_type_label,
      };
    case 'nzx_filings':
      return {
        ...result,
        attachmentname: getAttachmentName(result),
        company_name: result.company_name || '',
        object_location: result.link_to_report,
        mime_type_label: result?.mime_type_label,
      };
    case 'bse_annual_reports':
      return {
        company_name: result.company_name || '',
        object_location: result.link_to_report,
        industry: result.industry,
        instrument: result.instrument,
        listed: result.listed,
        cin: result.cin,
        year: result.year,
        security_code: result.security_code,
        security_id: result.security_id,
        doc_id: result.doc_id,
        deeplink: result.deeplink,
        access_key: result.access_key,
        board_id: result.board_id,
        channel_id: result.channel_id,
        unix: result.unix,
        mime_type: result.mime_type,
        category: 'bse_annual_reports',
        category_tab: result.category_tab,
        selected: result.selected,
        source: 'bse_annual_reports',
        image_location: result.image_location,
        attachmentname: getAttachmentName(result), // attachment always exist
        indices: result.indices,
        secondary_category: result.secondary_category_ua,
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
        highlights: result.highlights,
        link_to_pdf: result.link_to_pdf,
        external_access_link: result.external_access_link,
        comment_id: result?.comment_id,
        num_pages: result?.num_pages,
      };
    case 'bse_presentations':
      return {
        newssub: result.newssub,
        company_name: result.company_name || '',
        object_location: result.link_to_report,
        doc_id: result.doc_id,
        deeplink: result.deeplink,
        access_key: result.access_key,
        board_id: result.board_id,
        channel_id: result.channel_id,
        cin: result.cin,
        unix: result.unix,
        mime_type: result.mime_type,
        category: 'bse_presentations',
        category_tab: result.category_tab,
        selected: result.selected,
        source: 'bse_presentations',
        image_location: result.image_location,
        attachmentname: getAttachmentName(result),
        indices: result.indices,
        secondary_category: result.secondary_category_ua,
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
        highlights: result.highlights,
        link_to_pdf: result.link_to_pdf,
        external_access_link: result.external_access_link,
        comment_id: result?.comment_id,
        num_pages: result?.num_pages,
      };
    case 'bse_filings':
      return {
        newssub: result.newssub,
        headline: result.headline,
        company_name: result.company_name || '',
        object_location: result.link_to_report,
        doc_id: result.doc_id,
        deeplink: result.deeplink,
        access_key: result.access_key,
        board_id: result.board_id,
        channel_id: result.channel_id,
        cin: result.cin,
        unix: result.unix,
        mime_type: result.mime_type,
        category: 'bse_filings',
        category_tab: result.category_tab,
        selected: result.selected,
        source: 'bse_filings',
        image_location: result.image_location,
        attachmentname: getAttachmentName(result),
        sentiment_link: result.sentiment_link, // exists for conference call type filings
        secondary_category: result.secondary_category_ua,
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
        indices: result.indices,
        highlights: result.highlights,
        link_to_pdf: result.link_to_pdf,
        external_access_link: result.external_access_link,
        comment_id: result?.comment_id,
        num_pages: result?.num_pages,
      };
    case 'creditratings':
      return {
        ...result,
        attachmentname: getAttachmentName(result),
        company_name: (result?.company_name || result?.title) ?? '',
        object_location: result?.link as string,
        mime_type_label: result?.mime_type_label,
      };
    case 'bse_sdd_sast':
      return {
        ...result,
        attachmentname: getAttachmentName(result),
        company_name: result.company_name || '',
        object_location: result.link_to_report,
        category: 'bse_sdd_sast',
        source: 'bse_sdd_sast',
        secondary_category: Array.isArray(result.secondary_category_ua)
          ? result.secondary_category_ua
          : [result.secondary_category_ua],
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
      };
    case 'webclips':
    case 'drives': {
      const mime = result.mime_type?.toLowerCase() ?? '';
      return {
        ...result,
        source: [...new Set(result.retained_sources)]
          .filter((source) => source.toLowerCase() !== 'deleted')
          .map((source) => source.replace(/objects$/i, ''))[0],
        object_location: ['png', 'jpg', 'jpeg'].includes(mime)
          ? result.object_location
          : type === 'doc'
          ? result.object_location
          : result.image_location?.[0],
        mime_type_label: result?.mime_type_label,
      };
    }
    case 'regulators':
      return {
        ...result,
        object_location: Array.isArray(result.link)
          ? result.link[0]
          : result.link,
        category: 'regulators',
        headline: result.body_parsed,
        secondary_category: result.secondary_category_ua,
        secondary_categories_labels: result?.secondary_categories_labels,
        mime_type_label: result?.mime_type_label,
      };

    default:
      return result;
  }
};

const getAttachmentName = (result: FeedItemType): string => {
  return (
    result.attachmentname ||
    (result.image_location?.[0] ? result.doc_id : undefined)
  );
};
